// React
import { ReactElement, useMemo } from "react";
// Redux
import { useDispatch, useSelector } from "react-redux";
// Lodash
import debounce from "lodash/debounce";
// The same searchable select the KYC occupation field uses.
import CustomSelect from "../select";
// Actions
import { fetchOccupationList } from "../../../actions";
// Scss (reuse the standard input container/label/error styling)
import styles from "./Input.module.scss";

// Minimum characters typed before the occupation search endpoint is queried.
const MIN_SEARCH_LEN = 3;

// redux-form passes `input` ({ value, onChange, onBlur, name }) and `meta`.
type Props = {
  input: any;
  meta: { touched: any; error: any; warning: any };
  label?: string;
  required?: boolean;
  placeholder?: string;
  disabled?: boolean;
};

/**
 * Occupation field with server-side search, identical in behaviour to the KYC
 * occupation field: the user types (>= 3 chars) and picks a match returned by
 * the occupation endpoint. Wraps the shared CustomSelect and bridges it to
 * redux-form so it can be dropped in as a `<Field component={...}>`.
 */
export const ConnectedOccupationInput = ({
  input,
  meta: { touched, error },
  label,
  required,
  placeholder,
  disabled,
}: Props): ReactElement => {
  const dispatch = useDispatch();

  const occupationList = useSelector(
    (state: any) => state.employeeReducer?.kycDetails?.occupationList
  );

  // Server-side search: as the user types (>= 3 chars) fetch matches (debounced)
  // rather than filtering a full list client-side.
  const debouncedOccupationSearch = useMemo(
    () =>
      debounce((term: string) => {
        dispatch(fetchOccupationList(term));
      }, 300),
    [dispatch]
  );

  const handleInputChange = (inputValue: string, meta: any): void => {
    // Only fetch on actual typing - not on menu close, blur, or selection.
    if (meta && meta.action !== "input-change") return;
    const term = (inputValue || "").trim();
    if (term.length >= MIN_SEARCH_LEN) {
      debouncedOccupationSearch(term);
    }
  };

  const fetched = Array.isArray(occupationList)
    ? occupationList.map((item: any) => {
        const name = typeof item === "string" ? item : item?.name;
        return { label: name, value: name };
      })
    : [];

  // Keep the already-selected value visible even before a new search runs
  // (the search results replace the list as the user types).
  const options =
    input.value && !fetched.some((o: any) => o.value === input.value)
      ? [{ label: input.value, value: input.value }, ...fetched]
      : fetched;

  return (
    <div className={styles.inputContainer}>
      {label && (
        <div className={styles.labelContainer}>
          <div className={styles.label}>
            {label}
            {required && <span style={{ color: "#d0021b" }}> *</span>}
          </div>
        </div>
      )}
      <CustomSelect
        label=""
        name={input.name}
        placeholder={placeholder ?? "Type to search occupations..."}
        onChange={(value: any): void => input.onChange(value)}
        onInputChange={handleInputChange}
        filterOption={(): boolean => true}
        options={options}
        value={input.value}
        isClearable={false}
        disabled={disabled}
      />
      {touched && error && <span className={styles.error}>{error}</span>}
    </div>
  );
};

export default ConnectedOccupationInput;
